import type { PhoneNumber } from "libphonenumber-js";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import parseMax from "libphonenumber-js/max";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { configurationManager } from "../../../../../../shared/core/service/services";
import { CONFIGURATION_MEDIA } from "../../../../../../shared/domains/configuration/configuration";
import type { InitialOnboarding } from "../../../../../../shared/domains/onboarding/onboarding";
import { useObservable } from "../../../../../../shared/utils/observable";
import { BluePrimaryButton, WhitePrimaryButton } from "../../../../common/buttons/primary-button";
import { SwitchLabel } from "../../../../common/buttons/switch-button";
import { ErrorMessage } from "../../../../common/error-message";
import FormLabel from "../../../../common/forms/form-label";
import { MarkdownText } from "../../../../common/forms/markdown-text";
import { PhoneInput } from "../../../../common/forms/phone-input";
import { TextInput } from "../../../../common/forms/text-input";
import { theme } from "../../../../styles/theme";
import { RegisterPrivacyPolicy } from "../../register/components/register-privacy-policy";
import { RegisterTermOfServiceWidget } from "../../register/components/register-term-of-service";

const OnboardingInitialForm: React.FC<{
  value: InitialOnboarding;
  onChange?: (data: InitialOnboarding) => void;
  onPhoneNumberChange?: (phoneNumber: PhoneNumber) => void;
  onCancel: () => void;
  onSubmit: (data: InitialOnboarding) => void;
  initialPhoneNumber?: PhoneNumber;
  errorMessage?: string | null;
  loading?: boolean;
}> = ({ value, loading, errorMessage, initialPhoneNumber, onCancel, onChange, onSubmit }) => {
  const configuration = useObservable(configurationManager.configuration);
  const defaultCountry = useObservable(configurationManager.defaultCountry);
  const [countryCode, setCountryCode] = useState(initialPhoneNumber?.country ?? defaultCountry);
  const [number, setPhoneNumber] = useState<string>((value.phoneNumber?.number as string) || "");
  const [error, setError] = useState<string | null>(null);
  const [isTermAccepted, setIsTermAccepted] = useState<boolean>(false);
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const [needDistributorSelectionDuringOnboarding, setNeedDistributorSelectionDuringOnboarding] = useState(false);

  useEffect(() => {
    configurationManager.reload(CONFIGURATION_MEDIA.WEB_CLIENT).then(function () {
      setNeedDistributorSelectionDuringOnboarding(configurationManager.needDistributorSelectionDuringOnboarding());
    });
  }, []);

  useEffect(() => {
    const phoneNumber = parsePhoneNumberFromString(number, countryCode);
    onChange?.({
      ...value,
      phoneNumber: phoneNumber,
    });
  }, [countryCode, number]);

  const handleChange = (name: string, val: string) => {
    onChange?.({
      ...value,
      [name]: val,
    });
  };

  const isFormValid = () => {
    if (!value.firstName || !value.lastName || !value.email || !value.phoneNumber) {
      return false;
    }
    if (configuration.privacyPolicyUrl && !isPrivacyAccepted) {
      return false;
    }
    return configuration.termsAndConditionsUrl ? isTermAccepted : true;
  };

  const checkIsTaxedNumber = () => {
    const ACCEPTED_TYPES = ["MOBILE", "FIXED_LINE", "FIXED_LINE_OR_MOBILE"];
    const phone = parsePhoneNumberFromString(number, countryCode);
    const type = parseMax(phone?.number)?.getType();
    return {
      accepted: ACCEPTED_TYPES.includes(type),
      type: type,
    };
  };

  const handleSubmit = () => {
    const checkTaxedNumber = checkIsTaxedNumber();
    if (!checkTaxedNumber.accepted) {
      setError(formatMessage(`onboardingScreen.taxedNumberError.${checkTaxedNumber.type}`));
    } else {
      onSubmit(value);
    }
  };

  const handleTermsChange = () => {
    onChange?.({
      ...value,
      termsAcceptance: !isTermAccepted,
    });
    setIsTermAccepted(!isTermAccepted);
  };

  const handlePrivacyChange = () => {
    onChange?.({
      ...value,
      privacyPolicyAcceptance: !isPrivacyAccepted,
    });
    setIsPrivacyAccepted(!isPrivacyAccepted);
  };

  return (
    <>
      <Container>
        <StepTitle>{formatMessage("onboardingScreen.initialStepTitle")}</StepTitle>
        <FormFieldWrapper>
          <FormLabel label="onboardingScreen.formLabels.firstName" />
          <TextInput
            disabled={loading}
            value={value.firstName}
            onChange={(text) => handleChange("firstName", text.target.value)}
          />
        </FormFieldWrapper>
        <FormFieldWrapper>
          <FormLabel label="onboardingScreen.formLabels.lastName" />
          <TextInput
            disabled={loading}
            value={value.lastName}
            onChange={(text) => handleChange("lastName", text.target.value)}
          />
        </FormFieldWrapper>
        <PhoneFieldWrapper>
          <FormLabel label="onboardingScreen.formLabels.phoneNumber" />
          <PhoneInput
            maxWidth="0"
            disabled={loading}
            onChangeCountryCode={setCountryCode}
            onChangePhoneNumber={setPhoneNumber}
            initialCountryCode={initialPhoneNumber?.country ?? defaultCountry}
            initialPhone={initialPhoneNumber?.nationalNumber.toString() ?? ""}
          />
        </PhoneFieldWrapper>
        <FormFieldWrapper>
          <FormLabel label="onboardingScreen.formLabels.email" />
          <TextInput
            disabled={loading}
            type="email"
            value={value.email}
            onChange={(text) => handleChange("email", text.target.value)}
          />
        </FormFieldWrapper>
        {needDistributorSelectionDuringOnboarding && (
          <FormFieldWrapper>
            <FormLabel label="onboardingScreen.formLabels.selectDistributorDuringOnboarding" />
            <TextInput
              disabled={loading}
              type="text"
              value={value.distributorConfigurationCode}
              onChange={(text) => handleChange("distributorConfigurationCode", text.target.value)}
            />
            {/* Guidance, not a fault: as an errorMessage it only showed once the field was in
                error, which this one never is, so it was never read. */}
            <HelperText>
              {formatMessage("onboardingScreen.formLabels.selectDistributorDuringOnboardingHelperText")}
            </HelperText>
          </FormFieldWrapper>
        )}

        {configuration.termsAndConditionsUrl && (
          <StyledRegisterTermOfServiceWidgetWrapper>
            <RegisterTermOfServiceWidget
              showTitle={false}
              isTermAccepted={isTermAccepted}
              onTermChange={handleTermsChange}
              url={configuration.termsAndConditionsUrl}
              hasEula={!!configuration.termsAndConditionsUrl}
            />
          </StyledRegisterTermOfServiceWidgetWrapper>
        )}
        {configuration.privacyPolicyUrl && (
          <StyledRegisterTermOfServiceWidgetWrapper>
            <RegisterPrivacyPolicy
              showTitle={false}
              isTermAccepted={isPrivacyAccepted}
              onTermChange={handlePrivacyChange}
              url={configuration.privacyPolicyUrl}
              hasEula={!!configuration.privacyPolicyUrl}
            />
          </StyledRegisterTermOfServiceWidgetWrapper>
        )}
        {!!(error || errorMessage) && <ErrorMessage>{error || errorMessage}</ErrorMessage>}
        <ButtonContainer>
          <CancelButton onClick={() => onCancel?.()} size="S">
            {formatMessage("onboardingScreen.cancelButton")}
          </CancelButton>
          <SubmitButton
            disabled={!isFormValid() || loading}
            onClick={() => {
              try {
                handleSubmit();
              } catch (e) {
                setError(e);
              }
            }}
            size="S"
          >
            {formatMessage("onboardingScreen.getStartedButton")}
          </SubmitButton>
        </ButtonContainer>
      </Container>
    </>
  );
};

export default OnboardingInitialForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 460px;
`;

const StepTitle = styled(MarkdownText)`
  ${theme.boldText};
  align-self: flex-start;
  margin-bottom: 32px;
  font-size: 32px;
  line-height: 42px;
  color: ${theme.colors.blue[800]};
`;

/* Each field brings its own padded box; on a white page only the outline was missing, which left
   the fields standing invisible. */
const FormFieldWrapper = styled.div`
  margin-bottom: 20px;

  label {
    ${theme.text};
    font-size: 14px;
    line-height: 18px;
    color: ${theme.colors.blue[800]};
  }

  input {
    ${theme.text};
    font-size: 15px;
    color: ${theme.colors.blue[800]};
  }

  > div {
    width: 100%;
    border: 1px solid ${theme.colors.gray[300]};
    border-radius: 8px;
  }
`;

const HelperText = styled.span`
  ${theme.text};
  display: block;
  margin-top: 6px;
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

/* The phone stacks a country and a number, so the outline belongs to each of its two rows. */
const PhoneFieldWrapper = styled(FormFieldWrapper)`
  > div {
    border: 0;
  }

  > div > div {
    width: 100%;
    overflow: hidden;
    border: 1px solid ${theme.colors.gray[300]};
    border-radius: 8px;
  }

  > div > div:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
`;

const CancelButton = styled(WhitePrimaryButton)`
  flex: 1;
  padding: 13px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};
`;

const SubmitButton = styled(BluePrimaryButton)`
  flex: 1;
  padding: 13px;
  box-shadow: none;
`;

/* A panel of its own, as the redesign frames a block of text one has to read before agreeing. */
const StyledRegisterTermOfServiceWidgetWrapper = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;

  /* The widget keeps its own look where the register flow shows it alone; inside this panel the
     two lines and the switch are sized like the rest of the form. */
  > div > div > div {
    ${theme.text};
    margin-bottom: 0;
    font-size: 15px;
    line-height: 20px;
    color: ${theme.colors.blue[800]};
  }

  > div > div > div:last-child {
    margin-top: 8px;
    font-size: 13px;
    line-height: 18px;
    color: ${theme.colors.gray[800]};
  }

  a {
    color: ${theme.colors.blue[700]};
  }

  /* The consent sits across the panel, the switch answering the label from the far side. */
  > div > button {
    width: 100%;
    max-width: none;
    justify-content: space-between;
    margin: 16px 0 0;
    padding: 0;
  }

  ${SwitchLabel} {
    ${theme.text};
    font-size: 14px;
    line-height: 18px;
    text-align: left;
    color: ${theme.colors.blue[800]};
  }
`;
